import { Asset } from "../../types";
import { formatApy } from "~/utils/functions/formatApy";
import { getAssetURL } from "~/utils/getAssetURL";
import { Link } from "~/components/Link";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { getMeasureValue, MeasureNames } from "~/utils/getMeasureValue";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";
import React from "react";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { SortDirections } from "~/modules/filters/types";

// // // //

interface MarqueeItem {
    symbol: string;
    price: number;
    change: null | number;
    asset: { ticker: null | string; slug: null | string };
}

export interface MarqueeTickerGroup {
    label?: string;
    items: MarqueeItem[];
}

interface Props {
    groups: Array<{
        label?: string;
        count?: number;
        assets: Asset[];
        measure: MeasureNames;
    }>;
}

export function getTickerGroups(props: Props): MarqueeTickerGroup[] {
    const { groups } = props;

    const tickerGroups: MarqueeTickerGroup[] = groups.reduce(
        (all: MarqueeTickerGroup[], group) => {
            // Filter the assets in each group so only valid entries are included
            const filteredAssets = group.assets.filter((a) => {
                // Skip assets where ticker is null
                if (a.ticker === null) {
                    return false;
                }

                // Pull the measure value for each asset
                const measureValue = getMeasureValue(a, group.measure);

                // Include asset if measureValue is defined
                return measureValue !== null;
            });

            // Build a ticker item for each filtered asset
            const assetTickerItems: MarqueeItem[] = filteredAssets.map((a) => {
                const measureValue = getMeasureValue(a, group.measure);

                return {
                    symbol: a.ticker as string,
                    price: measureValue?.val as number,
                    change: measureValue?.chg_7d_pct ?? null,
                    asset: { ticker: a.ticker, slug: a.slug },
                };
            });

            // Get the count of assets for each group (defaults to 10)
            const count = group.count || 10;

            // Define TickerGroup object
            const tickerGroup: MarqueeTickerGroup = {
                label: group.label,
                items: sortItemsByProperty({
                    items: assetTickerItems,
                    field: "price",
                    direction: SortDirections.desc,
                }).slice(0, count),
            };

            return [...all, tickerGroup];
        },
        [],
    );

    return tickerGroups;
}

// // // //

export function AssetTickerMarquee(props: { groups: MarqueeTickerGroup[] }) {
    const { groups } = props;

    // Return null if there is no stockData to display
    if (groups.length === 0) {
        return null;
    }

    return (
        <div className="w-full bg-card overflow-hidden border-b sticky top-nav-height z-240 select-none">
            <div
                className="flex animate-scroll whitespace-nowrap divide-x hover:[animation-play-state:paused]"
                // ENHANCEMENT - update scroll speed scale with the page width
                // style={{
                //     animation: `scroll ${windowSize.width / 10}s linear infinite`,
                // }}
            >
                {groups.map((group, index) => (
                    <React.Fragment key={`group-${index}`}>
                        {group.label && (
                            <div
                                key={`group-${group.label}-${index}`}
                                className="px-4 flex items-center gap-2 text-foreground text-sm font-semibold py-1.5"
                            >
                                {group.label}
                            </div>
                        )}
                        {group.items.map((stock) => (
                            <div
                                key={stock.symbol}
                                className="px-4 flex items-center gap-2 text-sm font-light py-1.5"
                            >
                                <Link
                                    href={getAssetURL({ asset: stock.asset })}
                                    variant="default"
                                    analyticsLabel={
                                        AssetUserEvents.TickerLinkClick
                                    }
                                >
                                    {stock.symbol}
                                </Link>
                                <span className="text-foreground">
                                    ${formatCurrencyValueCompact(stock.price)}
                                </span>
                                {stock.change !== null && (
                                    <span
                                        className={`${stock.change >= 0 ? "text-change-up" : "text-change-down"}`}
                                    >
                                        {stock.change >= 0 ? "+" : ""}
                                        {formatApy(stock.change)}%
                                    </span>
                                )}
                            </div>
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}
